import { createSelector } from '@reduxjs/toolkit';
import { visibilityAtZoom, type ScaleVisibility } from '@okmaps/shared';
import type { RootState } from '../../app/store.js';

/**
 * Every read a component makes goes through this file.
 *
 * Components do not reach into state shape directly. That is what lets the
 * slices be reshaped without touching the render layer, and it is where
 * memoization lives so a map pan does not re-render the catalog tree.
 */

export const selectPanel = (s: RootState) => s.ui.panel;
export const selectDrawerOpen = (s: RootState) => s.ui.drawerOpen;
export const selectSearchText = (s: RootState) => s.ui.searchText;
export const selectCategory = (s: RootState) => s.ui.category;
export const selectRenderableOnly = (s: RootState) => s.ui.renderableOnly;
export const selectColorMode = (s: RootState) => s.ui.colorMode;
export const selectMetadataFor = (s: RootState) => s.ui.metadataFor;

export const selectActiveLayers = (s: RootState) => s.layers.active;
export const selectFocusedLayer = (s: RootState) => s.layers.focused;

export const selectMapCenter = (s: RootState) => s.map.center;
export const selectMapZoom = (s: RootState) => s.map.zoom;
export const selectBasemap = (s: RootState) => s.map.basemap;
export const selectPendingExtent = (s: RootState) => s.map.pendingExtent;
export const selectMapReady = (s: RootState) => s.map.ready;
export const selectIdentifyAt = (s: RootState) => s.map.identifyAt;

export const selectMeasureTool = (s: RootState) => s.measure.tool;
export const selectMeasureVertices = (s: RootState) => s.measure.vertices;
export const selectMeasureResult = (s: RootState) => s.measure.result;
export const selectMeasureEngine = (s: RootState) => s.measure.engine;

/** Names only, so the sync layer can diff cheaply without deep comparison. */
export const selectActiveLayerNames = createSelector(
  [selectActiveLayers],
  (layers) => layers.map((l) => l.name),
);

export const selectIsLayerActive = createSelector(
  [selectActiveLayers, (_: RootState, name: string) => name],
  (layers, name) => layers.some((l) => l.name === name),
);

/**
 * Where each active layer sits relative to its own scale window, right now.
 *
 * Keyed by layer name so a list item reads one entry rather than recomputing
 * the comparison per render. Layers with no window are absent from the map,
 * which callers read as unrestricted.
 *
 * This is what lets the interface say *why* a layer that is switched on is not
 * on screen. Without it the two cases - outside its scale window, and upstream
 * cannot draw it - look identical, and both look like a broken application.
 */
export const selectLayerScaleVisibility = createSelector(
  [selectActiveLayers, selectMapZoom],
  (layers, zoom) => {
    const out = new Map<string, ScaleVisibility>();
    for (const layer of layers) {
      if (layer.minScale === undefined && layer.maxScale === undefined) continue;
      out.set(layer.name, visibilityAtZoom(layer, zoom));
    }
    return out;
  },
);

/** Layers eligible for identify: on the map, visible, and queryable upstream. */
export const selectQueryableLayers = createSelector(
  [selectActiveLayers],
  (layers) => layers.filter((l) => l.visible && l.queryable),
);

/** The catalog query the API layer should run, derived from UI state. */
export const selectCatalogQuery = createSelector(
  [selectSearchText, selectCategory, selectRenderableOnly],
  (text, category, renderableOnly) => ({
    ...(text ? { text } : {}),
    ...(category ? { category } : {}),
    ...(renderableOnly ? { renderableOnly } : {}),
  }),
);

/**
 * What the panel shows: the live value while the pointer is still moving,
 * falling back to the last committed result. The spoken readout deliberately
 * uses `selectMeasureResult` instead, so a screen reader hears the total once
 * per vertex rather than on every mouse move.
 */
export const selectMeasureDisplay = createSelector(
  [(s: RootState) => s.measure.preview, selectMeasureResult],
  (preview, result) => preview ?? result,
);

/** The three unit controls, grouped so a panel reads them in one subscription. */
export const selectMeasureUnits = createSelector(
  [
    (s: RootState) => s.measure.system,
    (s: RootState) => s.measure.lengthUnit,
    (s: RootState) => s.measure.areaUnit,
  ],
  (system, lengthUnit, areaUnit) => ({ system, lengthUnit, areaUnit }),
);
